import { 
  collection, 
  doc, 
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION_NAME = 'stateAllocations';

export const stateAllocationService = {
  
  // PROJECT-LEVEL LOCK: Lock a project allocation
  async lockProjectAllocation(allocationId, userId, userName) {
    try {
      if (!allocationId || !userId) {
        throw new Error('Allocation ID and user ID are required');
      }

      const allocationRef = doc(db, COLLECTION_NAME, allocationId);
      const allocationDoc = await getDoc(allocationRef);
      
      if (!allocationDoc.exists()) {
        throw new Error('Allocation not found');
      }

      const allocationData = allocationDoc.data();
      if (allocationData.isLocked) {
        throw new Error('Allocation is already locked');
      }

      const lockData = {
        isLocked: true,
        lockedAt: serverTimestamp(),
        lockedBy: userId,
        lockedByName: userName || 'User',
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(allocationRef, lockData);
      console.log('Project allocation locked:', allocationId);
      return { success: true, message: 'Project locked successfully', data: lockData };
    } catch (error) {
      console.error('Error locking project allocation:', error);
      throw new Error(error.message || 'Failed to lock project');
    }
  },

  // PROJECT-LEVEL UNLOCK: Unlock a project allocation
  async unlockProjectAllocation(allocationId, userId, userName) {
    try {
      if (!allocationId || !userId) {
        throw new Error('Allocation ID and user ID are required');
      }

      const allocationRef = doc(db, COLLECTION_NAME, allocationId);
      const allocationDoc = await getDoc(allocationRef);
      
      if (!allocationDoc.exists()) {
        throw new Error('Allocation not found');
      }

      const allocationData = allocationDoc.data();
      if (!allocationData.isLocked) {
        throw new Error('Allocation is not locked');
      }

      const unlockData = {
        isLocked: false,
        lockedAt: null,
        lockedBy: null,
        lockedByName: null,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };

      await updateDoc(allocationRef, unlockData);
      console.log('Project allocation unlocked:', allocationId);
      return { success: true, message: 'Project unlocked successfully', data: unlockData };
    } catch (error) {
      console.error('Error unlocking project allocation:', error);
      throw new Error(error.message || 'Failed to unlock project');
    }
  },

  // Add new project allocation
  async addProjectAllocation(allocationData, userId) {
    try {
      const dataToSave = {
        ...allocationData,
        financialYear: allocationData.financialYear,
        isLocked: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      };
      
      const docRef = await addDoc(collection(db, COLLECTION_NAME), dataToSave);
      console.log('Project allocation added with ID:', docRef.id);
      return { success: true, data: { id: docRef.id, ...dataToSave } };
    } catch (error) {
      console.error('Error adding project allocation:', error);
      throw new Error(error.message || 'Failed to add project allocation');
    }
  },

  // Update project allocation - with lock check
  async updateProjectAllocation(allocationId, allocationData, userId) {
    try {
      const allocationRef = doc(db, COLLECTION_NAME, allocationId);
      const allocationDoc = await getDoc(allocationRef);
      
      if (allocationDoc.exists()) {
        const existingData = allocationDoc.data();
        if (existingData.isLocked) {
          throw new Error('Cannot update allocation: This project is locked. Contact admin to unlock.');
        }
      }
      
      const updateData = {
        ...allocationData,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };
      
      await updateDoc(allocationRef, updateData);
      console.log('Project allocation updated:', allocationId);
      return { success: true, data: updateData };
    } catch (error) {
      console.error('Error updating project allocation:', error);
      throw new Error(error.message || 'Failed to update project allocation');
    }
  },

  // Delete project allocation - with lock check
  async deleteProjectAllocation(allocationId) {
    try {
      const allocationRef = doc(db, COLLECTION_NAME, allocationId);
      const allocationDoc = await getDoc(allocationRef);
      
      if (allocationDoc.exists()) {
        const allocationData = allocationDoc.data();
        if (allocationData.isLocked) {
          throw new Error('Cannot delete allocation: This project is locked. Contact admin to unlock.');
        }
      }
      
      await deleteDoc(allocationRef);
      console.log('Project allocation deleted:', allocationId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting project allocation:', error);
      throw new Error(error.message || 'Failed to delete project allocation');
    }
  },

  // Get all project allocations
  async getProjectAllocations(filters = {}) {
    try {
      let allocationsQuery = collection(db, COLLECTION_NAME);
      const constraints = [];
      
      if (filters.financialYear) constraints.push(where('financialYear', '==', filters.financialYear));
      if (filters.department) constraints.push(where('department', '==', filters.department));
      
      constraints.push(orderBy('department', 'asc'));
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) allocationsQuery = query(allocationsQuery, ...constraints);
      
      const querySnapshot = await getDocs(allocationsQuery);
      const allocations = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        allocations.push({
          id: doc.id,
          ...data,
          isLocked: data.isLocked || false,
          lockedAt: data.lockedAt?.toDate() || null,
          lockedBy: data.lockedBy || null,
          lockedByName: data.lockedByName || null,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        });
      });
      
      return { success: true, data: allocations };
    } catch (error) {
      console.error('Error fetching project allocations:', error);
      throw new Error('Failed to fetch project allocations');
    }
  },

  // Get project allocation by ID
  async getProjectAllocationById(allocationId) {
    try {
      const allocationRef = doc(db, COLLECTION_NAME, allocationId);
      const allocationDoc = await getDoc(allocationRef);
      
      if (!allocationDoc.exists()) {
        throw new Error('Project allocation not found');
      }
      
      const data = allocationDoc.data();
      return {
        success: true,
        data: {
          id: allocationDoc.id,
          ...data,
          isLocked: data.isLocked || false,
          lockedAt: data.lockedAt?.toDate() || null,
          lockedBy: data.lockedBy || null,
          lockedByName: data.lockedByName || null,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        }
      };
    } catch (error) {
      console.error('Error fetching project allocation:', error);
      throw new Error('Failed to fetch project allocation');
    }
  },

  // Get departmental allocation summary
  async getDepartmentalAllocationSummary(financialYear = null) {
    try {
      let allocationsQuery = collection(db, COLLECTION_NAME);
      const constraints = [];
      
      if (financialYear) constraints.push(where('financialYear', '==', financialYear));
      if (constraints.length > 0) allocationsQuery = query(allocationsQuery, ...constraints);
      
      const querySnapshot = await getDocs(allocationsQuery);
      const departmentSummary = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const department = data.department;
        
        if (!departmentSummary[department]) {
          departmentSummary[department] = {
            department: department,
            clearedAgainstAllocation: 0,
            clearedFromCommonPool: 0,
            additionalStateShare: 0,
            projectCount: 0
          };
        }
        
        const dept = departmentSummary[department];
        dept.clearedAgainstAllocation += parseFloat(data.clearedAgainstAllocation) || 0;
        dept.clearedFromCommonPool += parseFloat(data.clearedFromCommonPool) || 0;
        dept.additionalStateShare += parseFloat(data.additionalStateShare) || 0;
        dept.projectCount += 1;
      });
      
      return { 
        success: true, 
        data: Object.values(departmentSummary).sort((a, b) => a.department.localeCompare(b.department))
      };
    } catch (error) {
      console.error('Error fetching departmental summary:', error);
      throw new Error('Failed to fetch departmental allocation summary');
    }
  },

  // Get allocation statistics
  async getAllocationStatistics() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
      
      const stats = {
        total: 0,
        totalApprovedAmount: 0,
        totalClearedAmount: 0,
        totalCommonPoolAmount: 0,
        totalAdditionalStateShare: 0,
        byDepartment: {},
        byFinancialYear: {},
        byInstallment: {}
      };
      
      querySnapshot.forEach((doc) => {
        const allocation = doc.data();
        
        stats.total++;
        stats.totalApprovedAmount += parseFloat(allocation.approvedAllocation) || 0;
        stats.totalClearedAmount += parseFloat(allocation.clearedAgainstAllocation) || 0;
        stats.totalCommonPoolAmount += parseFloat(allocation.clearedFromCommonPool) || 0;
        stats.totalAdditionalStateShare += parseFloat(allocation.additionalStateShare) || 0;
        
        if (allocation.department) {
          if (!stats.byDepartment[allocation.department]) {
            stats.byDepartment[allocation.department] = { count: 0, approvedAmount: 0, clearedAmount: 0 };
          }
          stats.byDepartment[allocation.department].count++;
          stats.byDepartment[allocation.department].approvedAmount += parseFloat(allocation.approvedAllocation) || 0;
          stats.byDepartment[allocation.department].clearedAmount += parseFloat(allocation.clearedAgainstAllocation) || 0;
        }
        
        if (allocation.financialYear) {
          if (!stats.byFinancialYear[allocation.financialYear]) {
            stats.byFinancialYear[allocation.financialYear] = { count: 0, approvedAmount: 0, clearedAmount: 0 };
          }
          stats.byFinancialYear[allocation.financialYear].count++;
          stats.byFinancialYear[allocation.financialYear].approvedAmount += parseFloat(allocation.approvedAllocation) || 0;
          stats.byFinancialYear[allocation.financialYear].clearedAmount += parseFloat(allocation.clearedAgainstAllocation) || 0;
        }
        
        if (allocation.installment) {
          if (!stats.byInstallment[allocation.installment]) {
            stats.byInstallment[allocation.installment] = { count: 0, amount: 0 };
          }
          stats.byInstallment[allocation.installment].count++;
          stats.byInstallment[allocation.installment].amount += parseFloat(allocation.totalAmount) || 0;
        }
      });
      
      return { success: true, data: stats };
    } catch (error) {
      console.error('Error fetching allocation statistics:', error);
      throw new Error('Failed to fetch allocation statistics');
    }
  },

  // Bulk operations
  async bulkUpdateAllocations(updates, userId) {
    try {
      const results = { successful: [], failed: [] };
      
      for (const update of updates) {
        try {
          const result = await this.updateProjectAllocation(update.id, update.data, userId);
          if (result.success) {
            results.successful.push(update.id);
          } else {
            results.failed.push({ id: update.id, error: 'Update failed' });
          }
        } catch (error) {
          results.failed.push({ id: update.id, error: error.message });
        }
      }
      
      return { success: true, data: results };
    } catch (error) {
      console.error('Error in bulk update:', error);
      throw new Error('Failed to perform bulk update');
    }
  },

  // Advanced filtering
  async getAllocationsWithAdvancedFilters(filters) {
    try {
      const allAllocations = await this.getProjectAllocations();
      let filteredAllocations = allAllocations.data;
      
      if (filters.amountRange) {
        const { min, max } = filters.amountRange;
        filteredAllocations = filteredAllocations.filter(allocation => {
          const amount = allocation.approvedAllocation || 0;
          return amount >= min && amount <= max;
        });
      }
      
      if (filters.installmentRange) {
        const { min, max } = filters.installmentRange;
        filteredAllocations = filteredAllocations.filter(allocation => {
          const installment = allocation.installment || 1;
          return installment >= min && installment <= max;
        });
      }
      
      if (filters.hasCommonPoolFunding !== undefined) {
        filteredAllocations = filteredAllocations.filter(allocation => {
          const hasCommonPool = (allocation.clearedFromCommonPool || 0) > 0;
          return hasCommonPool === filters.hasCommonPoolFunding;
        });
      }
      
      if (filters.fundingPattern) {
        filteredAllocations = filteredAllocations.filter(allocation => {
          if (!allocation.fundingPattern) return false;
          const { css, sms } = filters.fundingPattern;
          return allocation.fundingPattern.css === css && allocation.fundingPattern.sms === sms;
        });
      }
      
      return { success: true, data: filteredAllocations };
    } catch (error) {
      console.error('Error in advanced filtering:', error);
      throw new Error('Failed to perform advanced filtering');
    }
  }
};

export default stateAllocationService;